<template>
    <!-- MYAC-1522 block 4 — the chat's subject, chosen inside an opening that is not a
         project's own (the dashboard's): a question, then the choices as lines, like
         the opening's replies (Aymeric 2026-10-06, the dropdown was not elegant).
         Most accounts hold one to three; past five, « Un autre… » unfolds a search. -->
    <div class="scope-lines" data-testid="opening-scope-pick">
        <p class="question">{{ t(`chat.scope.pick-question.${tierKey}`) }}</p>
        <button v-for="c in listed" :key="c.id" type="button" class="scope-line" data-testid="scope-line"
            @click="emit('select', c.id)">
            <span class="truncate">{{ c.name }}</span>
            <i class="pi pi-arrow-right" aria-hidden="true" />
        </button>
        <!-- Nothing matches: said where the lines were, above the search. -->
        <p v-if="searching && matches.length === 0" class="none" data-testid="scope-none">{{ t(`chat.scope.no-match-tier.${tierKey}`) }}</p>
        <!-- Past five: the search itself, saying how many more it reaches (Aymeric
             2026-10-06: « on garde le 24 autres mais c'est directement la recherche »). -->
        <template v-if="hidden > 0">
            <label class="scope-search">
                <i class="pi pi-search" aria-hidden="true" />
                <input v-model="query" type="search" data-testid="scope-search"
                    :placeholder="t(`chat.scope.pick-other.${tierKey}`)" :aria-label="t(`chat.scope.pick-other.${tierKey}`)"
                    @keydown.enter.prevent="matches[0] && emit('select', matches[0].id)" />
                <small v-if="others > 0" data-testid="scope-others">{{ t('chat.scope.others', { n: others }, others) }}</small>
            </label>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { normalizeForSearch } from '@/utils/dashboardPortfolio'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    choices: { id: string, name: string }[]
    /** The tier the choices come from ('qms', 'td'…): names the question. */
    tier?: string | null
}>()
const emit = defineEmits<{ (e: 'select', id: string): void }>()
const { t } = useI18n()

/** Shown as lines before « Un autre… »; beyond, the search. */
const FIRST = 5
/** As many matches as lines before typing: the list never grows when you search. */
const MATCHES = FIRST

const tierKey = computed(() => (props.tier === 'qms' || props.tier === 'td' ? props.tier : 'other'))
const query = ref('')
/** Typing in the search: its matches replace the first lines. */
const searching = computed(() => query.value.trim().length > 0)

const fold = normalizeForSearch
const allMatches = computed(() => {
    const q = fold(query.value.trim())
    return q ? props.choices.filter(c => fold(c.name).includes(q)) : props.choices
})
const matches = computed(() => allMatches.value.slice(0, MATCHES))
const listed = computed(() => (searching.value ? matches.value : props.choices.slice(0, FIRST)))
const hidden = computed(() => Math.max(0, props.choices.length - FIRST))
/** What the lines do not show: the choices past five, or, while searching, the matches past five. */
const others = computed(() => Math.max(0, (searching.value ? allMatches.value.length : props.choices.length) - listed.value.length))
</script>

<style scoped>
/* overflow-x: the arrow's hover nudge must never widen the chat (a horizontal bar appeared). */
.scope-lines { display: flex; flex-direction: column; width: 100%; margin-top: 16px; overflow-x: hidden; }
.question { margin: 0 0 6px; color: var(--p-surface-700); }
.scope-line {
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    min-height: 48px; padding: 0 6px 0 0; border: 0; border-bottom: 1px solid var(--p-surface-200);
    background: transparent; font: inherit; color: var(--p-surface-800); text-align: left; cursor: pointer;
}
.scope-line:first-of-type { border-top: 1px solid var(--p-surface-200); }
.scope-line:hover { color: var(--p-primary-color); }
.scope-line .pi { font-size: 13px; transition: transform .2s ease-out; }
.scope-line:hover .pi { transform: translateX(4px); }
.scope-line.quiet { color: var(--p-surface-500); }
.scope-line.quiet small { margin-left: 6px; color: var(--p-surface-400); }
.scope-search {
    display: flex; align-items: center; gap: 10px; min-height: 48px;
    border-bottom: 1px solid var(--p-surface-200); color: var(--p-surface-500);
}
.scope-search small { flex-shrink: 0; padding-right: 6px; font-size: 13px; color: var(--p-surface-400); }
.scope-search input::placeholder { color: var(--p-surface-500); }
.scope-search input { flex: 1; min-width: 0; border: 0; padding: 0; font: inherit; color: var(--p-surface-800); background: transparent; }
.scope-search input:focus { outline: none; }
.scope-search input::-webkit-search-cancel-button { display: none; }
.none { display: flex; align-items: center; min-height: 48px; margin: 0; border-top: 1px solid var(--p-surface-200); border-bottom: 1px solid var(--p-surface-200); font-size: 14px; color: var(--p-surface-500); }
</style>
